import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAccount } from '../../hooks/useAccount'
import { api } from '../../services/api'
import { QuotaBadge } from '../shared/QuotaBadge'
import CreateStoriesTabs from './CreateStoriesTabs'

/**
 * CreateStoriesPage Component
 *
 * Main page for creating stories - supports both uploading new content
 * and reusing existing images with fresh captions.
 *
 * Features:
 * - Upload new images (AI-powered caption generation)
 * - Upload videos (feature-gated)
 * - Reuse existing images (A/B testing support)
 * - Quota badge showing remaining stories
 */
function CreateStoriesPage() {
  const { t } = useTranslation('upload')
  const { selectedAccount } = useAccount()
  const [quotaInfo, setQuotaInfo] = useState(null)

  // Load quota info
  // Phase 3: Load account-specific quota instead of user-level
  useEffect(() => {
    const loadQuota = async () => {
      // Only load if account is selected
      if (!selectedAccount?.id) {
        return
      }

      try {
        // Phase 3: Use account-level subscription API
        const subscriptionInfo = await api.getAccountSubscription(selectedAccount.id)
        // quotas is an object, not an array: { stories: {...}, regenerations: {...} }
        const storiesQuota = subscriptionInfo.quotas?.stories
        if (storiesQuota) {
          setQuotaInfo(storiesQuota)
        }
      } catch (error) {
        console.error('Failed to load quota info:', error)
      }
    }

    loadQuota()
  }, [selectedAccount?.id])

  if (!selectedAccount) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('pageTitle')}</h1>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 md:p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('noAccount.title')}</h3>
          <p className="text-gray-600">{t('noAccount.messageImages')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header with Quota Badge */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('pageTitle')}</h1>
          <p className="text-gray-600">
            {t('creatingContentFor', { username: selectedAccount.username || selectedAccount.instagram_username })}
          </p>
        </div>

        {/* Quota Badge */}
        {quotaInfo && (
          <QuotaBadge
            quotaType="stories"
            used={quotaInfo.used}
            limit={quotaInfo.limit}
            unlimited={quotaInfo.unlimited}
            resetsAt={quotaInfo.resets_at}
            onClick={() => window.location.href = '/subscription?tab=usage'}
          />
        )}
      </div>

      {/* Tabs: Upload New | Reuse Existing */}
      <CreateStoriesTabs accountId={selectedAccount.id} />
    </div>
  )
}

export default CreateStoriesPage
